import { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@jem-open/jem-ui';
import { Loader2, ArrowLeft, AlertTriangle } from 'lucide-react';
import useEmployerData from './hooks/useEmployerData';
import useCsvValidation from './hooks/useCsvValidation';
import useBulkSubmit from './hooks/useBulkSubmit';
import { Step } from './components/BulkUpload/constants';
import StepIndicator from './components/BulkUpload/StepIndicator';
import SelectEmployerStep from './components/BulkUpload/SelectEmployerStep';
import UploadStep from './components/BulkUpload/UploadStep';
import ReviewStep from './components/BulkUpload/ReviewStep';

const BulkUploadUsers = () => {
  const history = useHistory();
  const [step, setStep] = useState<Step>(Step.SelectEmployer);

  const {
    employers,
    loadingEmployers,
    selectedEmployer,
    setSelectedEmployer,
    availableRoles,
    availableFeatures,
    availableBranches,
    availableTags,
    loadingEmployerData,
  } = useEmployerData();

  const {
    file,
    fileError,
    validRows,
    errorRows,
    fileInputRef,
    handleFileChange,
    resetFile,
  } = useCsvValidation(
    availableRoles, availableFeatures, availableBranches, availableTags,
    () => setStep(Step.Review),
  );

  const { isSubmitting, handleSubmit } = useBulkSubmit({
    employerId: selectedEmployer?.value,
    validRows,
    availableRoles,
    availableFeatures,
    availableBranches,
    availableTags,
    onError: () => setStep(Step.Review),
  });

  const employerName = selectedEmployer?.label ?? '';
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const goToStep = (target: Step, reset = false) => {
    if (reset) resetFile();
    setStep(target);
  };

  const submitUsers = async () => {
    setShowConfirmDialog(false);
    setStep(Step.Submitting);
    const success = await handleSubmit();
    if (!success) setStep(Step.Review);
  };

  const onSubmit = async () => {
    if (step !== Step.Review) return;
    if (errorRows.length > 0) {
      setShowConfirmDialog(true);
      return;
    }
    await submitUsers();
  };

  const panels = useMemo(() => [
    { key: 'roles', label: 'Roles', items: availableRoles },
    { key: 'features', label: 'Features', items: availableFeatures },
    { key: 'receive_queries_from_branches', label: 'Receive Queries From Branches', items: availableBranches },
    { key: 'tags', label: 'Tags', items: availableTags },
  ], [availableRoles, availableFeatures, availableBranches, availableTags]);

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <div className="mb-4">
            <div className="flex justify-between items-start">
              <h2 className="mb-2 text-2xl font-semibold text-greyscale-text-title">
                Bulk Add Users
              </h2>
              <Button
                variant="link"
                size="large"
                leftIcon={<ArrowLeft className="size-[18px]" />}
                onClick={() => history.push('/admin/users')}
              >
                Back to Users
              </Button>
            </div>
            <p className="text-greyscale-text-caption font-medium mb-2">
              Upload a CSV file to create multiple users at once.
            </p>
            <StepIndicator currentStep={step} />
          </div>

          <div className="flex flex-col gap-md">
            {/* Step 1: Select Employer */}
            {step === Step.SelectEmployer && (
              <>
                <SelectEmployerStep
                  employers={employers}
                  selectedEmployer={selectedEmployer}
                  onEmployerChange={setSelectedEmployer}
                  loadingEmployerData={loadingEmployers || loadingEmployerData}
                  panels={panels}
                />
                <div className="flex justify-end mt-md">
                  <Button
                    variant="primary"
                    onClick={() => setStep(Step.Upload)}
                    disabled={!selectedEmployer || loadingEmployers || loadingEmployerData}
                  >
                    Continue
                  </Button>
                </div>
              </>
            )}

            {/* Step 2: Upload CSV */}
            {step === Step.Upload && (
              <>
                <UploadStep
                  employerName={employerName}
                  fileError={fileError}
                  file={file}
                  fileInputRef={fileInputRef}
                  onFileChange={handleFileChange}
                  onRemoveFile={resetFile}
                />
                <div className="flex justify-start mt-md">
                  <Button
                    variant="outline"
                    onClick={() => goToStep(Step.SelectEmployer, true)}
                  >
                    Back
                  </Button>
                </div>
              </>
            )}

            {/* Step 3: Review Upload */}
            {step === Step.Review && (
              <>
                <ReviewStep
                  employerName={employerName}
                  validRows={validRows}
                  errorRows={errorRows}
                />
                <div className="flex justify-between mt-md">
                  <Button
                    variant="outline"
                    onClick={() => goToStep(Step.Upload)}
                  >
                    Back
                  </Button>
                  <div className="flex gap-sm">
                    <Button
                      variant="secondary"
                      onClick={() => goToStep(Step.Upload, true)}
                    >
                      Re-upload
                    </Button>
                    <Button
                      variant="primary"
                      onClick={onSubmit}
                      disabled={validRows.length === 0 || isSubmitting}
                    >
                      Create {validRows.length} Users
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Step 4: Creating Users */}
            {step === Step.Submitting && (
              <div className="flex flex-col items-center py-xl">
                <Loader2 className="mb-sm h-8 w-8 animate-spin text-primary-text-label" />
                <p className="text-sm text-greyscale-text-caption">Creating {validRows.length} users...</p>
              </div>
            )}
          </div>

          <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
            <DialogContent className="p-sm">
              <DialogHeader>
                <DialogTitle>Some rows will be skipped</DialogTitle>
                <DialogDescription>
                  <AlertTriangle className="inline-block mr-xxs mb-xxxs size-4 text-warning-text-label" />
                  {errorRows.length} of {validRows.length + errorRows.length} rows have
                  errors and will not be created. Only{' '}
                  <strong>{validRows.length}</strong> valid{' '}
                  {validRows.length === 1 ? 'user' : 'users'} will be created.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="primary" size="medium" onClick={submitUsers}>
                  Continue
                </Button>
                <Button variant="outline" size="medium" onClick={() => setShowConfirmDialog(false)}>
                  Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Col>
      </Row>
    </Container>
  );
};

export default BulkUploadUsers;
